/*
  ASILI Product UI
  Dawn 15.3 product-page helper bundle.

  Goals:
  - keep Asili product UX functions out of global.js
  - require a deliberate size choice before add-to-cart
  - avoid long requestAnimationFrame enforcement loops
  - keep selected color label behavior intact
*/
(() => {
  if (window.__asiliProductUiInit) return;
  window.__asiliProductUiInit = true;

  const PRODUCT_ROOT_SELECTOR = 'product-info';
  const VARIANT_SELECTS_SELECTOR = 'variant-selects[id^="variant-selects-"], variant-selects[data-section]';
  const ASILI_MARKET_GUARD_ERROR_TEXT =
    'This color and size combination is not available for delivery to your region. Please choose another option.';

  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const cssEscape = (value) => {
    const stringValue = String(value || '');
    if (window.CSS?.escape) return CSS.escape(stringValue);
    return stringValue.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  };

  const getSectionIdFromVariantSelects = (variantSelects) => {
    if (!variantSelects) return null;
    if (variantSelects.dataset?.section) return variantSelects.dataset.section;
    if (variantSelects.id && variantSelects.id.startsWith('variant-selects-')) {
      return variantSelects.id.replace('variant-selects-', '');
    }
    return null;
  };

  const findVariantSelects = (sectionId, scope = document) => {
    if (!sectionId) return null;
    return (
      scope.querySelector?.(`#variant-selects-${cssEscape(sectionId)}`) ||
      scope.querySelector?.(`variant-selects[data-section="${cssEscape(sectionId)}"]`) ||
      document.querySelector(`#variant-selects-${cssEscape(sectionId)}`) ||
      document.querySelector(`variant-selects[data-section="${cssEscape(sectionId)}"]`)
    );
  };

  // =======================================================
  // 0) Market variant guard for EU-local production gaps
  // =======================================================
  const COLOR_HANDLES = new Set(['color', 'farbe', 'colour']);

  const parseBlockedColorHandles = (variantSelects) =>
    new Set(
      String(variantSelects?.dataset?.asiliMarketBlockedColors || '')
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean)
    );

  const isMarketGuardActive = (variantSelects) => variantSelects?.dataset?.asiliMarketGuardActive === 'true';

  const normalizeOptionHandle = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

  const getMarketGuardErrorText = (variantSelects) =>
    variantSelects?.dataset?.asiliMarketGuardError || ASILI_MARKET_GUARD_ERROR_TEXT;

  const parseMarketGuardVariants = (variantSelects) => {
    if (!variantSelects) return [];
    if (variantSelects.__asiliMarketGuardVariants) return variantSelects.__asiliMarketGuardVariants;

    const node = variantSelects.querySelector('script[data-asili-market-guard-variants]');
    if (!node?.textContent) {
      variantSelects.__asiliMarketGuardVariants = [];
      return variantSelects.__asiliMarketGuardVariants;
    }

    try {
      const parsed = JSON.parse(node.textContent);
      variantSelects.__asiliMarketGuardVariants = Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      variantSelects.__asiliMarketGuardVariants = [];
    }

    return variantSelects.__asiliMarketGuardVariants;
  };

  const parseMarketGuardRules = (variantSelects) => {
    if (!variantSelects) return [];
    if (variantSelects.__asiliMarketGuardRules) return variantSelects.__asiliMarketGuardRules;

    const node = variantSelects.querySelector('script[data-asili-market-guard-rules]');
    if (!node?.textContent) {
      variantSelects.__asiliMarketGuardRules = [];
      return variantSelects.__asiliMarketGuardRules;
    }

    try {
      const parsed = JSON.parse(node.textContent);
      variantSelects.__asiliMarketGuardRules = Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      variantSelects.__asiliMarketGuardRules = [];
    }

    return variantSelects.__asiliMarketGuardRules;
  };

  const getCurrentVariantId = (sectionId, productFormEl = null) => {
    const formInput =
      productFormEl?.querySelector?.('input[name="id"]') ||
      document.querySelector(`#product-form-${cssEscape(sectionId)} input[name="id"]`);

    return formInput?.value ? String(formInput.value) : '';
  };

  const isVariantBlockedForMarket = (variantSelects, variantId) => {
    if (!isMarketGuardActive(variantSelects) || !variantId) return false;

    const colorPosition = Number(variantSelects.dataset.asiliColorOptionPosition || 0);
    if (!colorPosition) return false;

    const variant = parseMarketGuardVariants(variantSelects).find((entry) => String(entry.id) === String(variantId));
    const colorValue = variant?.options?.[colorPosition - 1];
    if (!colorValue) return false;

    const colorHandle = normalizeOptionHandle(colorValue);
    if (parseBlockedColorHandles(variantSelects).has(colorHandle)) return true;

    const sizePosition = Number(variantSelects.dataset.asiliSizeOptionPosition || 0);
    const sizeValue = sizePosition ? String(variant.options?.[sizePosition - 1] || '').trim() : '';

    return parseMarketGuardRules(variantSelects).some((rule) => {
      if (normalizeOptionHandle(rule?.color) !== colorHandle) return false;
      if (rule?.sizes === '*') return true;
      if (!sizeValue) return false;

      const guardedSizes = Array.isArray(rule?.sizes) ? rule.sizes : [rule?.sizes];
      return guardedSizes.some((guardedSize) => String(guardedSize || '').trim() === sizeValue);
    });
  };

  const showMarketGuardError = (productFormEl, text = ASILI_MARKET_GUARD_ERROR_TEXT) => {
    if (!productFormEl) return;
    showError(productFormEl, text);
    const wrap = productFormEl.querySelector('.product-form__error-message-wrapper');
    if (wrap) wrap.dataset.asiliMarketGuardError = 'true';
  };

  const hideMarketGuardError = (productFormEl) => {
    if (!productFormEl) return;
    const wrap = productFormEl.querySelector('.product-form__error-message-wrapper');
    if (wrap?.dataset?.asiliMarketGuardError === 'true') {
      delete wrap.dataset.asiliMarketGuardError;
      hideError(productFormEl);
    }
  };

  const isCurrentVariantBlockedForMarket = (sectionId, productFormEl = null) => {
    const variantSelects = findVariantSelects(sectionId);
    return isVariantBlockedForMarket(variantSelects, getCurrentVariantId(sectionId, productFormEl));
  };

  const syncMarketGuardForSection = (sectionId) => {
    if (!sectionId) return;

    const productFormEl = document.querySelector(`product-form[data-section-id="${cssEscape(sectionId)}"]`);
    if (!productFormEl) return;

    if (isCurrentVariantBlockedForMarket(sectionId, productFormEl)) {
      productFormEl.toggleSubmitButton?.(true, window.variantStrings?.unavailable || 'Unavailable');
      showMarketGuardError(productFormEl, getMarketGuardErrorText(findVariantSelects(sectionId)));
    } else {
      hideMarketGuardError(productFormEl);
    }
  };

  const initMarketGuard = (scope = document) => {
    scope.querySelectorAll?.(VARIANT_SELECTS_SELECTOR).forEach((variantSelects) => {
      const sectionId = getSectionIdFromVariantSelects(variantSelects);
      if (sectionId) syncMarketGuardForSection(sectionId);
    });
  };

  const blockSubmitIfMarketBlocked = (event, form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    if (form.dataset.type !== 'add-to-cart-form') return false;

    const productFormEl = form.closest('product-form');
    const sectionId = productFormEl?.dataset?.sectionId;
    if (!sectionId) return false;

    if (isCurrentVariantBlockedForMarket(sectionId, productFormEl)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      showMarketGuardError(productFormEl, getMarketGuardErrorText(findVariantSelects(sectionId)));
      productFormEl.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      return true;
    }

    return false;
  };

  // =======================================================
  // 1) Selected color label next to Color/Farbe/Colour
  // =======================================================
  const SELECTED_COLOR_HANDLES = new Set(['color', 'farbe', 'colour']);

  const findLegend = (fieldset) =>
    fieldset?.querySelector('legend') ||
    fieldset?.querySelector('.form__label') ||
    fieldset?.querySelector('.product-form__input__label');

  const getSelectedValue = (fieldset) => {
    const checked = fieldset?.querySelector('input[type="radio"]:checked');
    if (checked?.value) return checked.value;

    const select = fieldset?.querySelector('select');
    if (select?.value) return select.value;

    return '';
  };

  const ensureSelectedSpan = (legend) => {
    let span = legend.querySelector('.asili-selected-value');
    if (!span) {
      span = document.createElement('span');
      span.className = 'asili-selected-value';
      legend.appendChild(span);
    }
    return span;
  };

  const updateSelectedColorFieldset = (fieldset) => {
    const handle = fieldset?.dataset?.optionHandle;
    if (!handle || !SELECTED_COLOR_HANDLES.has(handle)) return;

    const legend = findLegend(fieldset);
    if (!legend) return;

    ensureSelectedSpan(legend).textContent = getSelectedValue(fieldset);
  };

  const updateSelectedColorLabels = (scope = document) => {
    scope
      .querySelectorAll?.('.product-form__input[data-option-handle]')
      .forEach(updateSelectedColorFieldset);
  };

  // =======================================================
  // 2) Explicit Size Guard / SizeLock
  // =======================================================
  const SIZE_HANDLES = new Set(['size', 'groesse', 'grosse']);
  const ERROR_TEXT = 'Please select a size';
  const SELECT_SIZE_TEXT = 'SELECT A SIZE';
  const LOCK_CLASS = 'asili-size-lock';

  const sectionState = new Map();
  const settleTimers = new Map();

  const getState = (sectionId) => {
    if (!sectionState.has(sectionId)) {
      sectionState.set(sectionId, { sizeTouched: false, locked: true });
    }
    return sectionState.get(sectionId);
  };

  const syncLockClass = () => {
    const hasLockedSection = Array.from(sectionState.values()).some((state) => state.locked && !state.sizeTouched);
    document.documentElement.classList.toggle(LOCK_CLASS, hasLockedSection);
  };

  const findSizeFieldset = (variantSelects) => {
    if (!variantSelects) return null;
    return (
      Array.from(variantSelects.querySelectorAll('.product-form__input[data-option-handle]')).find((el) =>
        SIZE_HANDLES.has(el.dataset.optionHandle)
      ) || null
    );
  };

  const clearCheckedRadios = (fieldset) => {
    if (!fieldset) return false;

    let changed = false;
    fieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
      if (radio.checked || radio.hasAttribute('checked')) changed = true;
      radio.checked = false;
      radio.removeAttribute('checked');
    });

    return changed;
  };

  const isSizeChosen = (fieldset) => !!fieldset?.querySelector('input[type="radio"]:checked');

  const getProductForm = (sectionId, origin = null) =>
    origin?.closest?.('product-form') ||
    document.querySelector(`product-form[data-section-id="${cssEscape(sectionId)}"]`);

  const getAddToCartForm = (productFormEl) =>
    productFormEl?.querySelector?.('form[data-type="add-to-cart-form"]') ||
    productFormEl?.querySelector?.('form[action*="/cart/add"]') ||
    null;

  const getSubmitButton = (productFormEl) => {
    const form = getAddToCartForm(productFormEl);
    return (
      form?.querySelector?.('button[name="add"][type="submit"]') ||
      form?.querySelector?.('.product-form__submit') ||
      null
    );
  };

  const getButtonLabel = (button) => button?.querySelector?.('span') || button;

  const applySoftSizeLock = (sectionId, locked, origin = null) => {
    const productFormEl = getProductForm(sectionId, origin);
    if (!productFormEl) return;

    const button = getSubmitButton(productFormEl);
    const label = getButtonLabel(button);

    productFormEl.classList.toggle('asili-size-soft-lock', !!locked);
    productFormEl.dataset.asiliSizeSoftLock = locked ? 'true' : 'false';

    if (!button || !label) return;

    if (locked) {
      // Soft lock only: do NOT set button.disabled and do NOT disable the hidden variant id.
      // Dawn keeps its native availability/variant state; Asili only changes the visible CTA and blocks submit in capture phase.
      if (!button.dataset.asiliOriginalText) {
        button.dataset.asiliOriginalText = (label.textContent || '').replace(/\s+/g, ' ').trim();
      }
      button.dataset.asiliSizeSoftLocked = 'true';
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('asili-size-soft-lock__button');
      label.textContent = SELECT_SIZE_TEXT;
      return;
    }

    if (button.dataset.asiliSizeSoftLocked === 'true') {
      const originalText = button.dataset.asiliOriginalText || '';
      button.classList.remove('asili-size-soft-lock__button');
      button.removeAttribute('aria-disabled');
      delete button.dataset.asiliSizeSoftLocked;

      // Restore only our own label override. Do not force-enable or override Dawn's sold-out/unavailable state.
      if ((label.textContent || '').replace(/\s+/g, ' ').trim() === SELECT_SIZE_TEXT && originalText) {
        label.textContent = originalText;
      }
    }
  };

  const showError = (productFormEl, text) => {
    if (!productFormEl) return;
    const wrap = productFormEl.querySelector('.product-form__error-message-wrapper');
    const msg = productFormEl.querySelector('.product-form__error-message');
    if (!wrap || !msg) return;

    msg.textContent = text;
    wrap.hidden = false;
    wrap.removeAttribute('hidden');
  };

  const hideError = (productFormEl) => {
    if (!productFormEl) return;
    const wrap = productFormEl.querySelector('.product-form__error-message-wrapper');
    if (!wrap) return;
    wrap.hidden = true;
    wrap.setAttribute('hidden', 'hidden');
  };

  const clearSettleJobs = (sectionId) => {
    const jobs = settleTimers.get(sectionId) || [];
    jobs.forEach((job) => {
      if (job.type === 'raf') cancelAnimationFrame(job.id);
      if (job.type === 'timeout') clearTimeout(job.id);
    });
    settleTimers.delete(sectionId);
  };

  const clearSizeForSection = (sectionId, scope = document) => {
    const state = getState(sectionId);
    if (state.sizeTouched) return;

    const variantSelects = findVariantSelects(sectionId, scope);
    const sizeFieldset = findSizeFieldset(variantSelects);
    if (!sizeFieldset) return;

    state.locked = true;
    clearCheckedRadios(sizeFieldset);
    syncLockClass();
    applySoftSizeLock(sectionId, true, sizeFieldset);
  };

  const schedulePostRenderClear = (sectionId, scope = document) => {
    if (!sectionId) return;

    clearSettleJobs(sectionId);

    const jobs = [];
    const addRaf = () => {
      const id = requestAnimationFrame(() => clearSizeForSection(sectionId, scope));
      jobs.push({ type: 'raf', id });
    };
    const addTimeout = (delay) => {
      const id = setTimeout(() => clearSizeForSection(sectionId, scope), delay);
      jobs.push({ type: 'timeout', id });
    };

    // A few targeted settle checks cover Dawn/product-info updates without the old 80-120 RAF loop.
    clearSizeForSection(sectionId, scope);
    addRaf();
    addTimeout(60);
    addTimeout(160);
    addTimeout(360);

    settleTimers.set(sectionId, jobs);
  };

  const lockSectionUntilSizeChoice = (sectionId, scope = document) => {
    if (!sectionId) return;
    const state = getState(sectionId);
    state.sizeTouched = false;
    state.locked = true;
    syncLockClass();
    applySoftSizeLock(sectionId, true, scope instanceof Element ? scope : null);
    schedulePostRenderClear(sectionId, scope);

    const productFormEl = document.querySelector(`product-form[data-section-id="${cssEscape(sectionId)}"]`);
    hideError(productFormEl);
  };

  const markSizeChosen = (sectionId, origin = null) => {
    if (!sectionId) return;
    const state = getState(sectionId);
    state.sizeTouched = true;
    state.locked = false;
    clearSettleJobs(sectionId);
    syncLockClass();
    applySoftSizeLock(sectionId, false, origin);
    hideError(getProductForm(sectionId, origin));
  };

  const initSizeGuard = (scope = document) => {
    scope.querySelectorAll?.(VARIANT_SELECTS_SELECTOR).forEach((variantSelects) => {
      const sectionId = getSectionIdFromVariantSelects(variantSelects);
      if (!sectionId) return;

      const state = getState(sectionId);
      const sizeFieldset = findSizeFieldset(variantSelects);

      // Preserve an explicit user size choice across harmless DOM injections.
      if (state.sizeTouched && isSizeChosen(sizeFieldset)) {
        state.locked = false;
        syncLockClass();
        applySoftSizeLock(sectionId, false, variantSelects);
        return;
      }

      lockSectionUntilSizeChoice(sectionId, scope);
    });
  };

  const blockSubmitIfSizeMissing = (event, form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    if (form.dataset.type !== 'add-to-cart-form') return false;

    const productFormEl = form.closest('product-form');
    const sectionId = productFormEl?.dataset?.sectionId;
    if (!sectionId) return false;

    const variantSelects = findVariantSelects(sectionId);
    const sizeFieldset = findSizeFieldset(variantSelects);
    if (!sizeFieldset) return false;

    if (!isSizeChosen(sizeFieldset)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      applySoftSizeLock(sectionId, true, productFormEl);
      showError(productFormEl, ERROR_TEXT);
      sizeFieldset.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      return true;
    }

    return false;
  };

  // =======================================================
  // 3) Init + re-render hooks
  // =======================================================
  const initProductUi = (scope = document) => {
    updateSelectedColorLabels(scope);
    initSizeGuard(scope);
    initMarketGuard(scope);
  };

  onReady(() => initProductUi(document));
  window.addEventListener('pageshow', () => initProductUi(document));

  document.addEventListener('product-info:loaded', (event) => initProductUi(event.target || document));
  document.addEventListener('shopify:section:load', (event) => initProductUi(event.target || document));

  document.addEventListener('change', (event) => {
    const fieldset = event.target?.closest?.('.product-form__input[data-option-handle]');
    if (fieldset) updateSelectedColorFieldset(fieldset);
  });

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLElement)) return;

      const variantSelects = input.closest?.('variant-selects');
      if (!variantSelects) return;

      const sectionId = getSectionIdFromVariantSelects(variantSelects);
      if (!sectionId) return;

      const fieldset = input.closest?.('.product-form__input[data-option-handle]');
      const handle = fieldset?.dataset?.optionHandle;

      if (input instanceof HTMLInputElement && input.type === 'radio' && handle && SIZE_HANDLES.has(handle)) {
        markSizeChosen(sectionId, input);
        return;
      }

      const state = getState(sectionId);
      if (!state.sizeTouched) {
        lockSectionUntilSizeChoice(sectionId, variantSelects.closest(PRODUCT_ROOT_SELECTOR) || document);
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      if (blockSubmitIfMarketBlocked(event, event.target)) return;
      blockSubmitIfSizeMissing(event, event.target);
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const btn = event.target?.closest?.('.shopify-payment-button__button, .shopify-payment-button__more-options');
      if (!btn) return;

      const productFormEl = btn.closest('product-form');
      const sectionId = productFormEl?.dataset?.sectionId;
      if (!sectionId) return;

      if (isCurrentVariantBlockedForMarket(sectionId, productFormEl)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showMarketGuardError(productFormEl, getMarketGuardErrorText(findVariantSelects(sectionId)));
        return;
      }

      const variantSelects = findVariantSelects(sectionId);
      const sizeFieldset = findSizeFieldset(variantSelects);
      if (!sizeFieldset) return;

      if (!isSizeChosen(sizeFieldset)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        applySoftSizeLock(sectionId, true, productFormEl);
        showError(productFormEl, ERROR_TEXT);
      }
    },
    true
  );

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const sectionId = data?.sectionId;
      if (!sectionId) return;

      const state = getState(sectionId);
      if (!state.sizeTouched) schedulePostRenderClear(sectionId, document);
      updateSelectedColorLabels(document);
      syncMarketGuardForSection(sectionId);
    });
  }

  const observerRoot = document.querySelector('main') || document.body;
  if (observerRoot) {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;

          if (
            node.matches?.(PRODUCT_ROOT_SELECTOR) ||
            node.matches?.(VARIANT_SELECTS_SELECTOR) ||
            node.querySelector?.(`${PRODUCT_ROOT_SELECTOR}, ${VARIANT_SELECTS_SELECTOR}`)
          ) {
            initProductUi(node);
          }
        }
      }
    });

    observer.observe(observerRoot, { childList: true, subtree: true });
  }
})();
